import type { SelectOption } from 'naive-ui';

/**
 * Whether `value` is one of a string enum's values. Narrows raw strings
 * (localStorage, select callbacks, record keys from the API) to the enum
 * without an unchecked cast.
 *
 * @example
 * ```typescript
 * if (isEnumValue(ChatConfirmationMode, raw)) return raw; // raw: ChatConfirmationMode
 * ```
 */
export function isEnumValue<T extends Record<string, string>>(
  enumObject: T,
  value: unknown
): value is T[keyof T] {
  return Object.values<unknown>(enumObject).includes(value);
}

/**
 * Generic utility function to convert a TypeScript enum to SelectOption array
 * Uses the enum values directly as labels since they are already human-readable
 *
 * This function filters out numeric keys (for numeric enums) and only returns
 * string values, which are then used as both label and value in the SelectOption.
 *
 * @param enumObject - The enum object to convert (e.g., ClauseCategory, ContractManagementObligationType)
 * @returns Array of SelectOption objects with label and value set to the enum string values
 *
 * @example
 * ```typescript
 * enum MyEnum {
 *   OPTION_ONE = 'Option One',
 *   OPTION_TWO = 'Option Two',
 * }
 *
 * const options = enumToSelectOptions(MyEnum);
 * // Returns: [
 * //   { label: 'Option One', value: 'Option One' },
 * //   { label: 'Option Two', value: 'Option Two' },
 * // ]
 * ```
 */
export function enumToSelectOptions<T extends Record<string, string | number>>(
  enumObject: T
): SelectOption[] {
  // Get all enum values (filter out numeric keys for numeric enums)
  const values = Object.values(enumObject).filter(
    (value): value is string => typeof value === 'string'
  );

  return values.map(value => ({
    label: value,
    value,
  }));
}

/**
 * Converts various string formats (snake_case, camelCase, PascalCase, kebab-case) to Title Case
 *
 * Handles multiple formats:
 * - If the string already contains spaces, it's kept as-is (just capitalized)
 * - Otherwise:
 *   - Replaces underscores and hyphens with spaces
 *   - Inserts spaces before uppercase letters that follow lowercase letters (camelCase/PascalCase)
 *   - Capitalizes each word
 *
 * @param str - The string to convert
 * @returns Title Case string
 *
 * @example
 * ```typescript
 * snakeToTitleCase('waiting_for_signatures') // 'Waiting For Signatures' (snake_case)
 * snakeToTitleCase('waitingForSignatures') // 'Waiting For Signatures' (camelCase)
 * snakeToTitleCase('WaitingForSignatures') // 'Waiting For Signatures' (PascalCase)
 * snakeToTitleCase('not-yet-active') // 'Not Yet Active' (kebab-case)
 * snakeToTitleCase('not yet active') // 'Not Yet Active' (already has spaces)
 * snakeToTitleCase('contract') // 'Contract'
 * ```
 */
function snakeToTitleCase(str: string): string {
  // If the string already contains spaces, just capitalize each word
  if (str.includes(' ')) {
    return str
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  }

  // Replace underscores and hyphens with spaces
  let result = str.replace(/[_-]/g, ' ');

  // Insert spaces before uppercase letters that follow lowercase letters (camelCase/PascalCase)
  // This regex finds: lowercase letter followed by uppercase letter
  result = result.replace(/([a-z])([A-Z])/g, '$1 $2');

  // Capitalize each word
  return result
    .split(' ')
    .filter(word => word.length > 0) // Remove empty strings from multiple spaces
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

/**
 * Generic utility function to convert a TypeScript enum to SelectOption array with automatic label generation
 *
 * This function automatically converts enum values (e.g., 'waiting_for_signatures') to human-readable
 * labels (e.g., 'Waiting For Signatures'). You can optionally provide a label map to override specific labels.
 *
 * @param enumObject - The enum object to convert
 * @param labelOverrides - Optional map of enum values to custom labels (overrides automatic generation)
 * @returns Array of SelectOption objects with human-readable labels and enum values
 *
 * @example
 * ```typescript
 * enum MyEnum {
 *   OPTION_ONE = 'option_one',
 *   OPTION_TWO = 'option_two',
 * }
 *
 * // Automatic label generation
 * const options = enumToSelectOptionsWithLabels(MyEnum);
 * // Returns: [
 * //   { label: 'Option One', value: 'option_one' },
 * //   { label: 'Option Two', value: 'option_two' },
 * // ]
 *
 * // With custom overrides
 * const options2 = enumToSelectOptionsWithLabels(MyEnum, {
 *   'option_one': 'Custom Label One'
 * });
 * // Returns: [
 * //   { label: 'Custom Label One', value: 'option_one' },
 * //   { label: 'Option Two', value: 'option_two' },
 * // ]
 * ```
 */
export function enumToSelectOptionsWithLabels<T extends Record<string, string | number>>(
  enumObject: T,
  labelOverrides?: Record<string, string>
): SelectOption[] {
  // Get all enum values (filter out numeric keys for numeric enums)
  const values = Object.values(enumObject).filter(
    (value): value is string => typeof value === 'string'
  );

  return values.map(value => ({
    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must fall through to the next operand
    label: labelOverrides?.[value] || snakeToTitleCase(value),
    value,
  }));
}
